import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { logger } from '../../core/utils/logger';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ImageSelectOption } from '../images-select/image-select-option';
import { ImagesSelectComponent } from '../images-select';
import { TreatmentReturnRequestClient } from '../../core/client/treatment-return-request-client.service';
import { firstValueFrom } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';

interface FileUploadFailure {
    readonly file: ImageSelectOption;
    readonly message: string;
}

export interface TreatmentReturnFileUploadDialogData {
    files: ImageSelectOption[];
    treatmentId: string;
    treatmentReturnRequestId: number;
    treatmentTypeOriginalId?: number | null;
}

const selector = 'app-treatment-return-files-upload-dialog';

@Component({
    selector: selector,
    standalone: true,
    imports: [CommonModule, ButtonModule, ImagesSelectComponent, ToastModule],
    templateUrl: './treatment-return-files-upload-dialog.component.html',
    styleUrls: ['./treatment-return-files-upload-dialog.component.scss'],
})
export class TreatmentReturnFilesUploadDialogComponent implements OnInit {
    protected files?: ImageSelectOption[] | null;
    protected selectedFiles?: ImageSelectOption[] | null;
    protected isUploading = false;
    protected transferProgress = 0;
    protected completedFileCount = 0;
    protected totalFileCount = 0;
    protected uploadErrors: string[] = [];

    private readonly _dialogRef = inject(DynamicDialogRef, { optional: true });
    private readonly _dialogConfig = inject<DynamicDialogConfig<TreatmentReturnFileUploadDialogData>>(
        DynamicDialogConfig,
        {
            optional: true,
        }
    );
    private readonly _apiClient = inject(TreatmentReturnRequestClient);
    private readonly _toast = inject(MessageService);
    private readonly _fileProgress = new Map<ImageSelectOption, number>();
    private _hasSuccessfulUploads = false;

    ngOnInit(): void {
        this.files = this._dialogConfig?.data?.files;
        logger.logInformation(`[${selector}] opened`);
    }

    protected async onUpload(): Promise<void> {
        const { treatmentId, treatmentReturnRequestId } = this._dialogConfig?.data ?? {};
        const selectedFiles = this.selectedFiles;
        if (!treatmentId || !treatmentReturnRequestId || selectedFiles == null || selectedFiles.length <= 0) return;

        this.isUploading = true;
        this.completedFileCount = 0;
        this.totalFileCount = selectedFiles.length;
        this.transferProgress = 0;
        this.uploadErrors = [];
        this._fileProgress.clear();
        selectedFiles.forEach(file => this._fileProgress.set(file, 0));

        const failures = await this.uploadFilesWithConcurrency(treatmentId, treatmentReturnRequestId, selectedFiles, 2);
        const failedFiles = failures.map(failure => failure.file);
        const successfulFileCount = selectedFiles.length - failedFiles.length;
        this._hasSuccessfulUploads ||= successfulFileCount > 0;

        this._toast.add({
            severity: failedFiles.length > 0 ? 'error' : 'success',
            summary:
                failedFiles.length === selectedFiles.length
                    ? '附件上传失败'
                    : failedFiles.length > 0
                    ? '部分附件上传失败'
                    : '上传完成',
            detail: [
                successfulFileCount > 0 ? `${successfulFileCount} 个附件已上传美国平台，请在附件中查看` : '',
                failedFiles.length > 0 ? `${failedFiles.length} 个附件上传失败` : '',
            ]
                .filter(x => !!x)
                .join('\n'),
        });
        this.isUploading = false;
        this.transferProgress = 0;
        this._fileProgress.clear();
        if (failures.length === 0) {
            this._dialogRef?.close(true);
            return;
        }

        this.uploadErrors = failures.map(failure => `${this.getFileName(failure.file)}：${failure.message}`);
        this.files = [...failedFiles];
        this.selectedFiles = [...failedFiles];
    }

    private async uploadFilesWithConcurrency(
        treatmentId: string,
        treatmentReturnRequestId: number,
        files: ImageSelectOption[],
        concurrency: number
    ): Promise<FileUploadFailure[]> {
        const failures: FileUploadFailure[] = [];
        let nextFileIndex = 0;
        const uploadNext = async (): Promise<void> => {
            while (nextFileIndex < files.length) {
                const selectedFile = files[nextFileIndex++];
                try {
                    await this.uploadFileToDesignService(treatmentId, treatmentReturnRequestId, selectedFile);
                    this.setFileProgress(selectedFile, 100);
                } catch (reason: unknown) {
                    const message = this.getErrorMessage(reason);
                    logger.logError(
                        `[${selector}] Failed to upload file to design service, treatment: ${treatmentId}, file: ${this.getFileName(
                            selectedFile
                        )}, error: ${message}`
                    );
                    failures.push({ file: selectedFile, message });
                } finally {
                    this.completedFileCount += 1;
                }
            }
        };

        const workerCount = Math.min(Math.max(concurrency, 1), files.length);
        await Promise.all(Array.from({ length: workerCount }, () => uploadNext()));
        return failures;
    }

    private async uploadFileToDesignService(
        treatmentId: string,
        treatmentReturnRequestId: number,
        file: ImageSelectOption
    ): Promise<void> {
        const preparation = await firstValueFrom(
            this._apiClient.prepareFileUploadToDesignService(
                treatmentId,
                treatmentReturnRequestId,
                file.fileUrl,
                file.fileName
            )
        );

        const fileBlob = await firstValueFrom(
            this._apiClient.downloadFile(file.fileUrl, progress => this.setFileProgress(file, progress * 0.4))
        );

        await firstValueFrom(
            this._apiClient.uploadFileToDesignService(
                preparation.uploadUrl,
                fileBlob,
                preparation.contentType || fileBlob.type || 'application/octet-stream',
                progress => this.setFileProgress(file, 40 + progress * 0.55)
            )
        );

        this.setFileProgress(file, 95);
        await firstValueFrom(
            this._apiClient.completeFileUploadToDesignService(treatmentId, treatmentReturnRequestId, {
                uploadToken: preparation.uploadToken,
            })
        );
    }

    private setFileProgress(file: ImageSelectOption, progress: number): void {
        this._fileProgress.set(file, Math.round(progress));
        this.updateTransferProgress();
    }

    private updateTransferProgress(): void {
        const progressValues = Array.from(this._fileProgress.values());
        this.transferProgress =
            progressValues.length === 0
                ? 0
                : Math.round(progressValues.reduce((total, progress) => total + progress, 0) / progressValues.length);
    }

    protected onCancel(): void {
        if (this.isUploading) return;

        logger.logInformation(`[${selector}] onCancel`);
        this._dialogRef?.close(this._hasSuccessfulUploads);
    }

    private getFileName(file: ImageSelectOption): string {
        if (file.fileName) return file.fileName;

        try {
            return decodeURIComponent(new URL(file.fileUrl).pathname.split('/').pop() || 'Attachment');
        } catch {
            return 'Attachment';
        }
    }

    private getErrorMessage(reason: unknown): string {
        if (typeof reason === 'string' && reason.trim()) return reason;
        if (!reason || typeof reason !== 'object') return '未知错误，请重试';

        const error = reason as Record<string, unknown>;
        const responseBody = error['error'];
        if (responseBody && typeof responseBody === 'object') {
            const problemDetails = responseBody as Record<string, unknown>;
            const detail = problemDetails['detail'];
            if (typeof detail === 'string' && detail.trim()) return detail;
        }
        if (typeof responseBody === 'string' && responseBody.trim()) return responseBody;

        const message = error['message'];
        return typeof message === 'string' && message.trim() ? message : '未知错误，请重试';
    }
}
